import React, { useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import { Box, ImageList, ImageListItem, ButtonBase } from "@mui/material";

function ThumbWithLoader({
                             src,
                             alt,
                             onClick,
                             radius,
                             sxButton,
                             sxImg,
                             loaded,
                             onLoaded,
                         }) {
    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                position: "relative",
                borderRadius: `${radius}px`,
                overflow: "hidden",
                display: "block",
                ...sxButton,
            }}
        >
            {/*  Shimmer Loader Card */}
            {!loaded && (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: `${radius}px`,
                        background: "rgba(255,255,255,0.06)",
                        backdropFilter: "blur(6px)",
                        // subtle border so it reads as a "card"
                        border: "1px solid rgba(255,255,255,0.10)",
                        overflow: "hidden",

                        // shimmer sweep
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(110deg, rgba(255,255,255,0.00) 0%, rgba(255,255,255,0.20) 45%, rgba(255,255,255,0.00) 80%)",
                            transform: "translateX(-60%)",
                            animation: "thumbShimmer 1.2s ease-in-out infinite",
                        },

                        // a little depth
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background:
                                "radial-gradient(80% 60% at 30% 25%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.00) 60%)",
                            opacity: 0.9,
                        },

                        "@keyframes thumbShimmer": {
                            "0%": { transform: "translateX(-70%)" },
                            "100%": { transform: "translateX(70%)" },
                        },
                    }}
                />
            )}

            <Box
                component="img"
                src={src}
                alt={alt}
                loading="lazy"
                onLoad={onLoaded}
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    opacity: loaded ? 1 : 0,
                    transition: "opacity 220ms ease",
                    ...sxImg,
                }}
            />
        </ButtonBase>
    );
}

export default function DrawerImageGallery({
                                               images = [],
                                               alt = "Gallery image",
                                               heroHeight = 420,
                                               radius = 24,
                                               thumbGap = 8,
                                               onHeroLoad,
                                               onHeroError,
                                               onImageClick,
                                               lightboxBackdrop = "rgba(0,0,0,0.92)",
                                               lightboxBlur = 0,
                                           }) {
    const [open, setOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    //  Track which thumbs have loaded
    const [thumbLoadedMap, setThumbLoadedMap] = useState({});

    const slides = useMemo(() => {
        return (images || [])
            .filter(Boolean)
            .map((img) => ({ src: img?.default || img }))
            .filter((s) => Boolean(s.src));
    }, [images]);

    const hasImages = slides.length > 0;
    const hasMultiple = slides.length > 1;

    if (!hasImages) return null;

    const hero = slides[0];
    const thumbs = slides.slice(1);

    //  Always show exactly up to 3 thumbs on ALL screen sizes
    const visibleThumbs = thumbs.slice(0, 3);

    const openAt = (index) => {
        setCurrentIndex(index);
        setOpen(true);
        if (onImageClick) onImageClick(index);
    };

    const markThumbLoaded = (key) => {
        setThumbLoadedMap((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    };

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: { xs: "1fr auto", lg: "1fr" },
                    alignItems: "start",
                }}
            >
                {/* HERO */}
                <ButtonBase
                    onClick={() => openAt(0)}
                    sx={{
                        width: "100%",
                        borderRadius: `${radius}px`,
                        overflow: "hidden",
                        display: "block",
                        textAlign: "left",
                    }}
                >
                    <Box
                        component="img"
                        src={hero.src}
                        alt={`${alt} 1`}
                        loading="lazy"
                        onLoad={onHeroLoad}
                        onError={onHeroError}
                        sx={{
                            width: "100%",
                            height: heroHeight,
                            objectFit: "contain",
                            display: "block",
                        }}
                    />
                </ButtonBase>

                {/* RIGHT-SIDE THUMBS (xs/sm/md only) */}
                {hasMultiple && (
                    <Box
                        sx={{
                            display: { xs: "flex", lg: "none" },
                            flexDirection: "column",
                            gap: 1,
                            pl: 1,
                        }}
                    >
                        {visibleThumbs.map((slide, i) => {
                            const actualIndex = i + 1;
                            const key = `thumb-${actualIndex}-${slide.src}`;
                            const loaded = Boolean(thumbLoadedMap[key]);

                            return (
                                <ThumbWithLoader
                                    key={key}
                                    src={slide.src}
                                    alt={`${alt} ${actualIndex + 1}`}
                                    radius={radius}
                                    loaded={loaded}
                                    onLoaded={() => markThumbLoaded(key)}
                                    onClick={() => openAt(actualIndex)}
                                    sxButton={{
                                        width: { xs: 76, sm: 90, md: 100 },
                                        height: { xs: 76, sm: 90, md: 100 },
                                    }}
                                    sxImg={{
                                        objectFit: "cover",
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                {/* BOTTOM THUMBS (lg/xl only) */}
                {hasMultiple && (
                    <Box sx={{ display: { xs: "none", lg: "block" } }}>
                        <ImageList
                            cols={Math.min(3, visibleThumbs.length)}
                            gap={thumbGap}
                            sx={{
                                width: "100%",
                                "&.MuiImageList-root": { overflow: "hidden" },
                            }}
                        >
                            {visibleThumbs.map((slide, i) => {
                                const actualIndex = i + 1;
                                const key = `thumb-${actualIndex}-${slide.src}`;
                                const loaded = Boolean(thumbLoadedMap[key]);

                                return (
                                    <ImageListItem key={key}>
                                        <ThumbWithLoader
                                            src={slide.src}
                                            alt={`${alt} ${actualIndex + 1}`}
                                            radius={radius}
                                            loaded={loaded}
                                            onLoaded={() => markThumbLoaded(key)}
                                            onClick={() => openAt(actualIndex)}
                                            sxButton={{
                                                width: "100%",
                                                height: 150,
                                            }}
                                            sxImg={{
                                                height: "100%",
                                                objectFit: "contain",
                                                objectPosition: "center",
                                            }}
                                        />
                                    </ImageListItem>
                                );
                            })}
                        </ImageList>
                    </Box>
                )}
            </Box>

            <Lightbox
                open={open}
                index={currentIndex}
                slides={slides}
                close={() => setOpen(false)}
                styles={{
                    container: {
                        backgroundColor: lightboxBackdrop,
                        backdropFilter: lightboxBlur ? `blur(${lightboxBlur}px)` : undefined,
                    },
                }}
            />
        </>
    );
}
